import React, { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Plus, UserPlus, Send } from 'lucide-react'
import toast from 'react-hot-toast'
import { showSuccess, showError } from '@/lib/swal'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { attendanceService } from '@/services/attendanceService'
import { employeesService } from '@/services/employeesService'
import type { AttendanceListRecord } from '@/api/types'
import type { Employee } from '@/api/types'
import type { DepartmentLookup } from '@/api/types'

function formatAttendanceDate(iso?: string | null): string {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  } catch {
    return String(iso).slice(0, 10)
  }
}

/** Convert local date + time to UTC ISO string for PostgreSQL timestamp with time zone. */
function toUTCISO(dateStr: string, timeHHmm: string): string {
  if (!dateStr || !timeHHmm || timeHHmm.length < 5) return ''
  const local = `${dateStr}T${timeHHmm}:00`
  return new Date(local).toISOString()
}

/** For input type="time" (HH:mm). */
function isoToTimeInput(iso?: string | null): string {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    const h = d.getHours()
    const m = d.getMinutes()
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
  } catch {
    return ''
  }
}

function formatTimeDisplay(iso?: string | null): string {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: true })
  } catch {
    return String(iso).slice(11, 16) || '—'
  }
}

const EXPECTED_START_TIME = '09:00'

/** Calculate totalAttendedHours, lateHoursCount, incompleteHours from Time In/Out and expected working hours. */
function calculateAttendanceMetrics(
  dateStr: string,
  timeInStr: string,
  timeOutStr: string,
  expectedWorkingHours: number
): { totalAttendedHours: number; lateHoursCount: number; incompleteHours: number } {
  if (!dateStr || !timeInStr || timeInStr.length < 5 || !timeOutStr || timeOutStr.length < 5) {
    return { totalAttendedHours: 0, lateHoursCount: 0, incompleteHours: expectedWorkingHours }
  }
  const dIn = new Date(`${dateStr}T${timeInStr}:00`)
  const dOut = new Date(`${dateStr}T${timeOutStr}:00`)
  const expectedStart = new Date(`${dateStr}T${EXPECTED_START_TIME}:00`)
  const actualAttendedHours = Math.max(0, (dOut.getTime() - dIn.getTime()) / 3600000)
  const lateHoursCount = dIn > expectedStart
    ? Math.round((dIn.getTime() - expectedStart.getTime()) / 3600000)
    : 0
  const incompleteHours = Math.max(0, Math.round(expectedWorkingHours - actualAttendedHours))
  return {
    totalAttendedHours: Math.round(actualAttendedHours * 10) / 10,
    lateHoursCount,
    incompleteHours,
  }
}

function mapToListRecord(r: any): AttendanceListRecord {
  return {
    id: r.id ?? 0,
    attendanceDate: r.attendanceDate ?? undefined,
    employeeCode: r.employeeCode ?? r.empCode ?? '',
    employeeName: r.employeeName ?? r.empName ?? '',
    timeIn: r.timeIn ?? null,
    timeOut: r.timeOut ?? null,
    isPresent: r.isPresent ?? 1,
    totalAttendedHours: r.totalAttendedHours ?? 0,
    lateHoursCount: r.lateHoursCount ?? 0,
    incompleteHours: r.incompleteHours ?? r.uncompleteHours ?? 0,
    isPosted: r.isPosted ?? false,
  }
}

function getDefaultDateRange(): { dateFrom: string; dateTo: string } {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return {
    dateFrom: `${y}-${m}-01`,
    dateTo: `${y}-${m}-${d}`,
  }
}

export const Attendance: React.FC = () => {
  const navigate = useNavigate()
  const { user, token } = useAuthStore((s) => ({ user: s.user, token: s.token }))
  const branchId = user?.selectedBranch?.branchId

  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState<AttendanceListRecord[]>([])
  const [search, setSearch] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [totalRecords, setTotalRecords] = useState(0)
  const [edits, setEdits] = useState<Record<number, { timeIn?: string; timeOut?: string; isPresent?: number }>>({})
  const [updatingId, setUpdatingId] = useState<number | null>(null)
  const [dateFrom, setDateFrom] = useState(() => getDefaultDateRange().dateFrom)
  const [dateTo, setDateTo] = useState(() => getDefaultDateRange().dateTo)
  const [departmentId, setDepartmentId] = useState(0)
  const [empId, setEmpId] = useState(0)
  const [departments, setDepartments] = useState<DepartmentLookup[]>([])
  const [employeesList, setEmployeesList] = useState<Employee[]>([])
  const [posting, setPosting] = useState(false)

  const loadData = async () => {
    if (!branchId) return
    setLoading(true)
    try {
      const res = await attendanceService.getListByFilters(branchId, {
        departmentId: departmentId || 0,
        empId: empId || 0,
        dateFrom,
        dateTo,
        pageNumber,
        pageSize,
      })
      setRows((res.data || []).map(mapToListRecord))
      setTotalRecords(res.totalRecords ?? 0)
      setEdits({})
    } catch (e: any) {
      const status = e?.response?.status
      const msg = (e?.response?.data?.message || '').toLowerCase()
      const isNoData = status === 404 || /not found|no data|no records|no attendance/.test(msg)
      setRows([])
      setTotalRecords(0)
      if (!isNoData) {
        toast.error(e?.response?.data?.message ?? 'Failed to load attendance.')
      }
    } finally {
      setLoading(false)
    }
  }

  const getRowTimeIn = (row: AttendanceListRecord) =>
    edits[row.id]?.timeIn ?? isoToTimeInput(row.timeIn)
  const getRowTimeOut = (row: AttendanceListRecord) =>
    edits[row.id]?.timeOut ?? isoToTimeInput(row.timeOut)
  const getRowIsPresent = (row: AttendanceListRecord) =>
    edits[row.id]?.isPresent ?? row.isPresent

  const setTimeIn = (id: number, value: string) => {
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], timeIn: value || undefined } }))
  }
  const setTimeOut = (id: number, value: string) => {
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], timeOut: value || undefined } }))
  }
  const setIsPresent = (id: number, value: number) => {
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], isPresent: value } }))
  }

  const getRowMetrics = (row: AttendanceListRecord) => {
    const dateStr = row.attendanceDate ? row.attendanceDate.slice(0, 10) : ''
    const timeInStr = getRowTimeIn(row)
    const timeOutStr = getRowTimeOut(row)
    const expectedWorkingHours = row.totalAttendedHours ?? 8
    if (!dateStr || !timeInStr || timeOutStr.length < 5) {
      return {
        totalAttendedHours: row.totalAttendedHours ?? 0,
        lateHoursCount: row.lateHoursCount ?? 0,
        incompleteHours: row.incompleteHours ?? 0,
      }
    }
    return calculateAttendanceMetrics(dateStr, timeInStr, timeOutStr, expectedWorkingHours)
  }

  const handleUpdateRow = async (row: AttendanceListRecord) => {
    const timeInStr = getRowTimeIn(row)
    const timeOutStr = getRowTimeOut(row)
    const dateStr = row.attendanceDate ? row.attendanceDate.slice(0, 10) : new Date().toISOString().slice(0, 10)
    const expectedWorkingHours = row.totalAttendedHours ?? 8
    const metrics = calculateAttendanceMetrics(dateStr, timeInStr, timeOutStr, expectedWorkingHours)
    const timeIn = timeInStr
      ? toUTCISO(dateStr, timeInStr)
      : (row.timeIn ? new Date(row.timeIn).toISOString() : '')
    const timeOut = timeOutStr
      ? toUTCISO(dateStr, timeOutStr)
      : (row.timeOut ? new Date(row.timeOut).toISOString() : '')
    const payload = {
      attendanceData: [
        {
          id: row.id,
          timeIn,
          timeOut,
          isPresent: getRowIsPresent(row),
          totalAttendedHours: metrics.totalAttendedHours,
          lateHoursCount: metrics.lateHoursCount,
          incompleteHours: metrics.incompleteHours,
        },
      ],
    }
    console.log('[Attendance Update] PUT /api/Attendance/Update — request body:', JSON.stringify(payload, null, 2))
    setUpdatingId(row.id)
    try {
      await attendanceService.update(payload)
      await showSuccess('Updated', 'Attendance time updated.')
      setEdits((prev) => {
        const next = { ...prev }
        delete next[row.id]
        return next
      })
      loadData()
    } catch (e: any) {
      await showError('Update failed', e?.response?.data?.message ?? e?.message ?? 'Failed to update.')
    } finally {
      setUpdatingId(null)
    }
  }

  useEffect(() => {
    setPageNumber(1)
  }, [branchId])

  useEffect(() => {
    if (!branchId) {
      setRows([])
      setTotalRecords(0)
      return
    }
    if (!token) return
    loadData()
  }, [branchId, token, pageNumber, pageSize, dateFrom, dateTo, departmentId, empId])

  useEffect(() => {
    if (!branchId) {
      setDepartments([])
      return
    }
    let cancelled = false
    employeesService.getDepartmentLookups().then((list) => {
      if (!cancelled && list) setDepartments(list)
    }).catch(() => { if (!cancelled) setDepartments([]) })
    return () => { cancelled = true }
  }, [branchId])

  useEffect(() => {
    if (!branchId) {
      setEmployeesList([])
      return
    }
    let cancelled = false
    employeesService.getAllEmployees(branchId, 1, 500).then((res) => {
      if (!cancelled && res?.data) setEmployeesList(res.data)
    }).catch(() => { if (!cancelled) setEmployeesList([]) })
    return () => { cancelled = true }
  }, [branchId])

  const employeeOptionsByDepartment = useMemo(() => {
    const nameFor = (emp: Employee) =>
      [emp.empFirstNameEn, emp.empFamilyNameEn].filter(Boolean).join(' ').trim() || emp.empCode || '—'
    if (!departmentId) {
      return employeesList.filter((emp) => emp.id && emp.empCode).map((emp) => ({
        value: String(emp.id),
        label: nameFor(emp),
      }))
    }
    return employeesList
      .filter((emp) => emp.empDepartmetId === departmentId && emp.id && emp.empCode)
      .map((emp) => ({
        value: String(emp.id),
        label: nameFor(emp),
      }))
  }, [employeesList, departmentId])

  const filteredRows = useMemo(() => {
    let list = rows
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter(
        (r) =>
          (r.employeeCode ?? '').toLowerCase().includes(q) ||
          (r.employeeName ?? '').toLowerCase().includes(q)
      )
    }
    return [...list].sort((a, b) => {
      const empA = (a.employeeCode ?? '') + (a.employeeName ?? '')
      const empB = (b.employeeCode ?? '') + (b.employeeName ?? '')
      if (empA !== empB) return empA.localeCompare(empB)
      const da = a.attendanceDate ?? ''
      const db = b.attendanceDate ?? ''
      return da.localeCompare(db)
    })
  }, [rows, search])

  const handlePostAttendance = async () => {
    if (!branchId) {
      toast.error('Select a branch first.')
      return
    }
    setPosting(true)
    try {
      await attendanceService.postAttendance(dateFrom, dateTo, departmentId || 0, branchId)
      await showSuccess('Posted', 'Attendance posted successfully.')
      loadData()
    } catch (e: any) {
      await showError('Post failed', e?.response?.data?.message ?? e?.message ?? 'Failed to post attendance.')
    } finally {
      setPosting(false)
    }
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title="Attendance"
        description="View and update attendance records."
      >
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="default"
            onClick={() => navigate('/payroll/attendance/combined/new')}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Attendance Combined
          </Button>
          <Button
            variant="default"
            onClick={() => navigate('/payroll/attendance/manual/new')}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add Attendance Manually
          </Button>
        </div>
      </PageHeader>

      <Card>
        <CardHeader>
          <CardTitle>Attendance records</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Date From</label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Date To</label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Department</label>
              <SearchableSelect
                options={[
                  { value: '0', label: 'All departments' },
                  ...departments.filter((d) => d.id).map((d) => ({
                    value: String(d.id),
                    label: d.lookupNameEn || d.lookupNameAr || String(d.id),
                  })),
                ]}
                value={departmentId ? String(departmentId) : '0'}
                onValueChange={(v) => {
                  setDepartmentId(v ? Number(v) : 0)
                  setEmpId(0)
                }}
                placeholder="All departments"
                searchPlaceholder="Search department..."
                emptyMessage="No department found"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Employee</label>
              <SearchableSelect
                options={[
                  { value: '0', label: 'All employees' },
                  ...employeeOptionsByDepartment,
                ]}
                value={empId ? String(empId) : '0'}
                onValueChange={(v) => setEmpId(v ? Number(v) : 0)}
                placeholder="All employees"
                searchPlaceholder="Search employee..."
                emptyMessage="No employee found"
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by code or name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button
                variant="default"
                disabled={!branchId || posting || loading}
                onClick={handlePostAttendance}
              >
                <Send className="w-4 h-4 mr-2" />
                {posting ? 'Posting...' : 'Post'}
              </Button>
            </div>
          </div>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-center">Employee Code</TableHead>
                  <TableHead className="text-center">Employee Name</TableHead>
                  <TableHead className="text-center">Attendance Date</TableHead>
                  <TableHead className="text-center">Time In</TableHead>
                  <TableHead className="text-center">Time Out</TableHead>
                  <TableHead className="text-center">Is Present</TableHead>
                  <TableHead className="text-center">Working Hours</TableHead>
                  <TableHead className="text-center">Late Hours</TableHead>
                  <TableHead className="text-center">Incomplete Hours</TableHead>
                  <TableHead className="text-center w-[100px]">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                      {!branchId
                        ? 'Select a branch first.'
                        : loading
                          ? 'Loading...'
                          : rows.length === 0
                            ? 'No attendance records.'
                            : 'No matching records.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRows.map((row, index) => (
                    <TableRow key={row.id ?? index}>
                      <TableCell className="text-center">{row.employeeCode ?? '—'}</TableCell>
                      <TableCell className="text-center">{row.employeeName ?? '—'}</TableCell>
                      <TableCell className="text-center">{formatAttendanceDate(row.attendanceDate)}</TableCell>
                      <TableCell className="text-center">
                        {row.isPosted ? (
                          formatTimeDisplay(row.timeIn)
                        ) : (
                          <input
                            type="time"
                            value={getRowTimeIn(row)}
                            onChange={(e) => setTimeIn(row.id, e.target.value)}
                            className="h-9 w-28 rounded border border-input bg-background px-2 text-center text-sm"
                          />
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {row.isPosted ? (
                          formatTimeDisplay(row.timeOut)
                        ) : (
                          <input
                            type="time"
                            value={getRowTimeOut(row)}
                            onChange={(e) => setTimeOut(row.id, e.target.value)}
                            className="h-9 w-28 rounded border border-input bg-background px-2 text-center text-sm"
                          />
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {row.isPosted ? (
                          getRowIsPresent(row) === 1 ? 'Present' : 'Absent'
                        ) : (
                          <Select
                            value={String(getRowIsPresent(row))}
                            onValueChange={(v) => setIsPresent(row.id, Number(v))}
                          >
                            <SelectTrigger className="h-9 w-28 mx-auto">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">Present</SelectItem>
                              <SelectItem value="0">Absent</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </TableCell>
                      <TableCell className="text-center">{row.totalAttendedHours ?? 0}</TableCell>
                      <TableCell className="text-center">{getRowMetrics(row).lateHoursCount}</TableCell>
                      <TableCell className="text-center">{getRowMetrics(row).incompleteHours}</TableCell>
                      <TableCell className="text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={row.isPosted || updatingId === row.id}
                          onClick={() => handleUpdateRow(row)}
                        >
                          {updatingId === row.id ? '...' : 'Update'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {/* Pagination */}
          {(totalRecords > 0 || pageNumber > 1) && (
            <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>
                  {totalRecords === 0
                    ? 'No records'
                    : `Showing ${(pageNumber - 1) * pageSize + 1}–${Math.min(pageNumber * pageSize, totalRecords)} of ${totalRecords}`}
                </span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(v) => {
                    setPageSize(Number(v))
                    setPageNumber(1)
                  }}
                >
                  <SelectTrigger className="w-[90px]">
                    <SelectValue placeholder="Size" />
                  </SelectTrigger>
                  <SelectContent>
                    {[10, 25, 50, 100].map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span>per page</span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pageNumber <= 1 || loading}
                  onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground px-2">
                  Page {pageNumber} of {Math.max(1, Math.ceil(totalRecords / pageSize))}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={
                    pageNumber >= Math.ceil(totalRecords / pageSize) || loading || totalRecords === 0
                  }
                  onClick={() => setPageNumber((p) => p + 1)}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
